import { Box, Collapse, Stack } from '@mui/material';
import { createPlanPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { isNotEmptyString, isNotNullOrUndefined } from '@vy/utils';
import type { ChangeEvent, PropsWithChildren } from 'react';
import React from 'react';
import type { FieldPath, FieldValues, PathValue } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { VyHint } from '../../../../../../components/new-design/data-display/VyHint';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { VyDescriptionTitle } from '../../../../../../components/new-design/data-display/VyDescriptionTitle/VyDescriptionTitle.tsx';
import { VySwitch } from '../../../../../../components/new-design/inputs/VySwitchNew.tsx';
import { SIZES } from '../../../../../../components/types/sizes.ts';

type ChargeEditDrawerContentItemOptionalProps<TForm extends FieldValues = PlanInputForm> = PropsWithChildren<{
  fieldPath: FieldPath<TForm>;
  label: string;
  labelDescription?: string | undefined;
  hint?: string;
  handleOnChecked?: (checked: boolean) => void;
  isChecked?: boolean;
  defaultValue?: string;
  disabled?: boolean;
}>;

export function ChargeEditDrawerContentItemOptional<TForm extends FieldValues = PlanInputForm>({
  fieldPath,
  label,
  labelDescription,
  hint,
  handleOnChecked,
  isChecked,
  defaultValue,
  children,
  disabled,
}: ChargeEditDrawerContentItemOptionalProps<TForm>): React.ReactElement {
  const { getValues, setValue } = useFormContext<TForm>();
  const doesHaveField = isNotNullOrUndefined(isChecked)
    ? isChecked
    : isNotNullOrUndefined(getValues(fieldPath));

  const handleFieldCheck = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (isNotNullOrUndefined(handleOnChecked)) {
      handleOnChecked(checked);
      return;
    }

    setValue(
      fieldPath,
      (checked ? (defaultValue ?? '') : undefined) as PathValue<TForm, FieldPath<TForm>>,
      { shouldDirty: true },
    );
  };

  return (
    <Stack direction="column" {...getSelectorAttribute(createPlanPageSelectors.drawer.settingOptions)}>
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <VySwitch
          checked={doesHaveField}
          onChange={handleFieldCheck}
          disabled={disabled}
          label={<VyDescriptionTitle title={label} subTitle={labelDescription} size={SIZES.SMALL} />}
          labelPlacement="start"
          labelWrapperSx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '100%',
            gap: 5,
          }}
        />

        {isNotEmptyString(hint) && (
          <Box sx={{ marginTop: '8px' }}>
            <VyHint tooltipContent={hint} />
          </Box>
        )}
      </Stack>
      {children && (
        <Collapse in={doesHaveField}>
          <Box sx={{ marginTop: '16px' }}>{children}</Box>
        </Collapse>
      )}
    </Stack>
  );
}
